import { get } from 'lodash';
import { EnvironmentCluster } from 'libs.react';
import { PATIENT_APP_NON_WEB_URLS_BY_ENV } from '../constants/patient_app_urls.constants';
export const isChinaCluster = (environment) => {
    return [EnvironmentCluster.CN_DEV, EnvironmentCluster.CN_PROD, EnvironmentCluster.CN_ALI_PLATFORM].includes(environment?.deployment?.cluster);
};
export const getMoaBaseUrl = (environment) => {
    const deploymentCluster = environment.deployment?.cluster;
    const environmentCluster = environment.cluster;
    const prodClusters = ['prod', 'eu1', 'dev', 'eu1-dev'];
    const chinaProdClusters = ['cn1', 'cn1-dev'];
    //  Check for china cluster and return appropriate url
    if (chinaProdClusters.includes(deploymentCluster) || chinaProdClusters.includes(environmentCluster)) {
        return 'api.medable.cn';
    }
    //  Check for prod cluster and return appropriate url
    if (prodClusters.includes(deploymentCluster) || prodClusters.includes(environmentCluster)) {
        return 'api.medable.com';
    }
    // Ephemeral environments should use production API when created from production Studio
    if (environment.deployment?.type === 'ephemeral' &&
        (deploymentCluster === 'ephemeral' || environmentCluster === 'ephemeral')) {
        return process.env.STUDIO_ENV === 'production' ? 'api.medable.com' : 'api-int-dev.medable.com';
    }
    //  Return default url
    return 'api-int-dev.medable.com';
};
export const getFinalUrl = (baseURL, encoded) => {
    return `https://${baseURL}/moa/routes/moa/gen/${encoded}`;
};
export const getStudyShortCode = async (environment) => {
    if (environment.cortexURL.includes('api.local.medable.com')) {
        return null;
    }
    const moaBaseUrl = getMoaBaseUrl(environment);
    const { cortexURL, code } = environment;
    const cortexHostname = cortexURL.replace('https://', '');
    const encoded = encodeURIComponent(`${cortexHostname}/${code}`);
    const fetchResponse = await fetch(getFinalUrl(moaBaseUrl, encoded));
    const response = await fetchResponse.json();
    return response?.data;
};
export const getOnboardingConfig = (study) => {
    const studyConfig = get(study, 'c_menu_config', []);
    return studyConfig.find((config) => config.c_menu === 'subject_onboarding');
};
/*
  cortexAPI urls: https://gitlab.medable.com/platform/services/mom/-/blob/main/config.json#L34-39
  webapp urls: https://gitlab.medable.com/devops/ops/-/blob/main/shared/nix/services/cortex/cortex/hosts.map.yaml
 */
// Environments on cloud.medable production infrastructure
const PROD_ENVS = ['dev', 'eu1', 'eu1-dev'];
// Non-production/testing environments
const NON_PROD_ENVS = ['int-dev', 'qa', 'test', 'local'];
export const parseCortexUrl = (cortexUrl) => {
    const url = new URL(cortexUrl);
    const { hostname } = url;
    // Extract the environment part between 'api' and '.medable' and TLD
    const envMatch = hostname.match(/^api([.-])?(.*)\.medable\.(com|cn|tech)$/);
    if (!envMatch) {
        return null;
    }
    const [, , env = '', tld] = envMatch;
    return { env, tld };
};
export const extractClassicAppsBaseUrl = (cortexUrl, appName) => {
    const parsed = parseCortexUrl(cortexUrl);
    if (!parsed) {
        return `https://${appName}.medable.com`;
    }
    const { env, tld } = parsed;
    // Production US/CN environments won't have env
    if (!env) {
        return `https://${appName}.medable.${tld}`;
    }
    // Determine the separator to use based on environment and app name
    let separator = '-'; // Default separator
    switch (true) {
        case env === 'local':
            separator = '.';
            break;
        // portal/mystudy in qa/dev in non-CN environments use dot for legacy reasons
        case ['portal', 'mystudy'].includes(appName) && (env === 'qa' || env === 'dev') && tld !== 'cn':
            separator = '.';
            break;
        default:
            separator = '-';
    }
    return `https://${appName}${separator}${env}.medable.${tld}`;
};
export const getPatientAppNonWebUrls = (cortexUrl) => {
    const parsed = parseCortexUrl(cortexUrl);
    if (!parsed) {
        return PATIENT_APP_NON_WEB_URLS_BY_ENV.DEFAULT;
    }
    const { env, tld } = parsed;
    // Production US/CN environments won't have env
    if (!env) {
        return tld === 'cn' ? PATIENT_APP_NON_WEB_URLS_BY_ENV.PROD_CN : PATIENT_APP_NON_WEB_URLS_BY_ENV.PROD;
    }
    // CN environments (all use same China App Store URL)
    if (tld === 'cn') {
        return PATIENT_APP_NON_WEB_URLS_BY_ENV.PROD_CN;
    }
    // Production infrastructure environments (cloud.medable)
    if (PROD_ENVS.includes(env)) {
        return PATIENT_APP_NON_WEB_URLS_BY_ENV.PROD;
    }
    // Non-production/testing environments → TestFlight
    if (NON_PROD_ENVS.includes(env)) {
        return PATIENT_APP_NON_WEB_URLS_BY_ENV.DEV;
    }
    // Unknown environments → fallback to DEFAULT
    return PATIENT_APP_NON_WEB_URLS_BY_ENV.DEFAULT;
};
